import { 
  getAuth, 
  signInAnonymously, 
  setPersistence, 
  browserLocalPersistence 
} from "firebase/auth";
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  setDoc, 
  addDoc, 
  serverTimestamp, 
  Timestamp,
  writeBatch
} from "firebase/firestore";
import {
  ref,
  get,
  query as dbQuery,
  orderByChild,
  equalTo
} from "firebase/database";
import moment from "moment";
import { Dispatch } from "redux";
import { v4 as uuidv4 } from "uuid";
import { db, rtdb } from "../firebase";
import {
	AnalyticsUser,
	ChannelData,
	ChatRequest,
	ChatRequestStatus,
	ChatUserData,
	ClientData,
	Content,
	ContentType,
	User,
} from "../interfaces";
import { Tag } from "../interfaces/Tag";
import { showModalAlert } from "../redux/Chat/actions";
import {
	setChannelData,
	setChannelDataLoading,
	setChannelId,
	setChannelsData,
	setChannelsDataLoading,
	setClientData,
	setClientDataLoading,
	setClientId,
	setContentData,
	setContentDataLoading,
	setContentId,
	setContentsData,
	setContentsDataLoading,
	setTagsData,
	setTagsDataLoading,
} from "../redux/Dashboard/actions";
import { initFirebasePresence } from "../services/firebasePresence";
import userHasRole from "./userHasRole";

const fetchClient = (clientId: string, dispatch: Dispatch, appUser: User | undefined) => {
	dispatch(setClientDataLoading(true));
	dispatch(setClientId(clientId));
	//fetch clientData
	const clientDocRef = doc(db, "clients", clientId);
	getDoc(clientDocRef)
		.then(docSnap => {
			if (docSnap.exists()) {
				const data: ClientData | undefined = docSnap.data() ? (docSnap.data() as ClientData) : undefined;
				if (appUser && userHasRole(data?.id || "", appUser, ["owner", "admin", "host", "superhost"])) {
					dispatch(setClientData(data));
				} else {
					window.location.href = "/dashboard";
				}
			}
		})
		.finally(() => {
			dispatch(setClientDataLoading(false));
		});
};

const fetchChannel = (channelId: string, dispatch: Dispatch) => {
	dispatch(setChannelDataLoading(true));
	dispatch(setChannelId(channelId));
	//fetch channelData
	const channelDocRef = doc(db, "channels", channelId);
	getDoc(channelDocRef)
		.then(docSnap => {
			let exists = false;
			if (docSnap.exists()) {
				const data: ChannelData | undefined = docSnap.data() ? (docSnap.data() as ChannelData) : undefined;
				if (!data?.versionCode) {
					exists = true;
					dispatch(setChannelData(data));
				}
			}
		})
		.finally(() => {
			dispatch(setChannelDataLoading(false));
		});
};

const fetchChannels = (clientId: string, dispatch: Dispatch) => {
	dispatch(setChannelsDataLoading(true));
	//fetch channelData
	const channelsQuery = query(
		collection(db, "channels"),
		where("clientId", "==", clientId)
	);
	getDocs(channelsQuery)
		.then(querySnapshot => {
			let exists = false;
			const payload: ChannelData[] = [];
			querySnapshot.forEach(doc => {
				const data: ChannelData = doc.data() as ChannelData;
				if (!data?.versionCode) {
					exists = true;
					payload.push(data);
				}
			});
			// Sort by displayOrder (missing values last), then name, so every consumer sees the Channels page order
			payload.sort((a, b) => {
				const aOrder = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
				const bOrder = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
				return aOrder - bOrder || (a.name || "").localeCompare(b.name || "");
			});
			dispatch(setChannelsData(payload));
		})
		.finally(() => {
			dispatch(setChannelsDataLoading(false));
		});
};

const fetchContent = async (contentId: string, dispatch: Dispatch) => {
	dispatch(setContentDataLoading(true));
	dispatch(setContentId(contentId));
	//fetch videoData

	let contentDoc;
	let interactionsCollection;

	let data: Record<string, any> = {};

	try {
		const contentDocRef = doc(db, "contents", contentId);
		contentDoc = await getDoc(contentDocRef);
		
		const interactionsQuery = collection(db, "interactions/contents", contentId);
		interactionsCollection = await getDocs(interactionsQuery);

		if (contentDoc.exists()) {
			if (!data?.versionCode) {
				data = { ...contentDoc.data() };
			}
		}
		if (!interactionsCollection.empty) {
			const interactions: any[] = [];
			interactionsCollection.forEach(i => {
				const interaction = i.data();
				if (!interaction.versionCode) {
					interactions.push({ ...i.data() });
				}
			});
			data = { ...data, interactions };
		} else {
			data = { ...data, interactions: [] };
		}

		dispatch(setContentData(data as Content));
	} catch (error) {
		console.warn(`Error getting content`, error);
	} finally {
		dispatch(setContentDataLoading(false));
	}
};

const fetchContents = (clientId: string, dispatch: Dispatch) => {
	dispatch(setContentsDataLoading(true));
	//fetch contentsData
	const contentsQuery = query(
		collection(db, "contents"),
		where("clientId", "==", clientId)
	);
	getDocs(contentsQuery)
		.then(querySnapshot => {
			let exists = false;
			const payload: Content[] = [];
			querySnapshot.forEach(doc => {
				const data: Content = doc.data() as Content;
				if (!data?.versionCode) {
					exists = true;
					payload.push(data);
				}
			});
			dispatch(setContentsData(payload));
		})
		.finally(() => {
			dispatch(setContentsDataLoading(false));
		});
};

const fetchTags = (clientId: string, dispatch: Dispatch) => {
	dispatch(setTagsDataLoading(true));
	//fetch contentsData
	const tagsCollectionRef = collection(db, "tags/clientTags", clientId);
	getDocs(tagsCollectionRef)
		.then(querySnapshot => {
			const payload: Tag[] = [];
			querySnapshot.forEach(doc => {
				payload.push({ ...(doc.data() as Tag) });
			});
			dispatch(setTagsData(payload));
		})
		.finally(() => {
			dispatch(setTagsDataLoading(false));
		});
};

const createChatRequest = async (
	contentId: string,
	uid: string,
	userName: string,
	message: string,
	image: string | null,
	status: ChatRequestStatus = ChatRequestStatus.request,
	sendMessage: boolean = false
) => {
	const contentChatCollection = collection(db, "chat/direct", contentId);
	const chatQuery = query(
		contentChatCollection,
		where("createdBy", "==", uid),
		where("status", "in", [0, 1, 6])
	);

	const querySnapshot = await getDocs(chatQuery);
	let create = querySnapshot.empty;

	if (!create) {
		create = true;
		querySnapshot.forEach(doc => {
			const data = doc.data() as ChatRequest;
			if (data.userData[uid].status !== ChatRequestStatus.ended) {
				create = false;
			}
		});
	}
	if (create) {
		const newChatRequest: ChatRequest = {
			id: uuidv4(),
			users: [uid],
			createdBy: uid,
			hosts: [],
			userName,
			message,
			status: status,
			userData: {
				[uid]: {
					name: userName,
					image,
					muted: false,
					status: status,
				},
			},
			timestamp: Timestamp.now(),
			showRequest: sendMessage,
			updatedAt: moment.utc().valueOf(),
			hostChat: true,
		};
		const chatDocRef = doc(contentChatCollection, newChatRequest.id);
		await setDoc(chatDocRef, newChatRequest)
			.then(() => {
				if (sendMessage) {
					const newMessage = {
						userName,
						uid,
						message,
						timestamp: serverTimestamp(),
						updatedAt: serverTimestamp(),
						photoURL: image ? `https://media.online.brushfire.com/profile_images/${uid}/profile.jpg` : null,
						isHost: false,
						flags: [],
						isDeleted: false,
					};
					const messageCollectionRef = collection(db, "chat/contents", newChatRequest.id);
					addDoc(messageCollectionRef, newMessage);
				}
			});
		return true;
	} else {
		return false;
	}
};

const createDirectMessageChatRequest = (
	contentId: string,
	uid: string,
	recipientUid: string,
	userName: string,
	recipientUserName: string,
	message: string,
	chatUserData: ChatUserData,
	status: ChatRequestStatus = ChatRequestStatus.request,
	isModerator: boolean = false, //True if the initiator is a moderator,
	dispatch: Dispatch,
	hostChat: boolean,
	setCurrentChat?: (chatRequest: ChatRequest) => void
) => {
	const userPresenceRef = ref(rtdb, `/analytics/user-presence/${contentId}`);
	const userPresenceQuery = dbQuery(userPresenceRef, orderByChild("firebaseId"), equalTo(recipientUid));
	get(userPresenceQuery)
		.then(snap => {
			const data: { state: string }[] = snap.exists() ? Object.values(snap.val() ? snap.val() : {}) : [];
			if (data.length === 1 && data[0].state === "offline") {
				dispatch(
					showModalAlert({
						showModal: true,
						modalData: {
							title: "Unavailable",
							message: `${recipientUserName} is currently offline`,
							closeButtonText: "Okay",
						},
					})
				);
			} else {
				const contentChatCollection = collection(db, "chat/direct", contentId);
				const chatQuery = query(
					contentChatCollection,
					where("users", "array-contains", uid),
					where("status", "in", [0, 1, 3, 4, 5])
				);

				getDocs(chatQuery)
					.then(existingChats => {
						let proceed = true;
						let reopened = false;
						existingChats.forEach(existingChat => {
							const existingRequest = existingChat.data() as ChatRequest;
							if (existingRequest.users.includes(recipientUid)) {
								proceed = false;
								if (
									existingRequest.status === ChatRequestStatus.directMessageEnded ||
									existingRequest.userData[uid].status === ChatRequestStatus.directMessageEnded
								) {
									if (reopened === false) {
										reopened = true;
										existingRequest.status = ChatRequestStatus.directMessageActive;
										existingRequest.userData[uid].status = ChatRequestStatus.directMessageActive;
										existingRequest.updatedAt = moment.utc().valueOf();
										const chatDocRef = doc(contentChatCollection, existingChat.id);
										setDoc(chatDocRef, existingRequest);
									}
								} else {
									if (!existingRequest.showRequest && existingRequest.createdBy !== uid) {
										if (reopened === false) {
											reopened = true;
											existingRequest.status = ChatRequestStatus.directMessageActive;
											existingRequest.userData[uid].status =
												ChatRequestStatus.directMessageActive;
											existingRequest.showRequest = true;
											const chatDocRef = doc(contentChatCollection, existingChat.id);
											setDoc(chatDocRef, existingRequest);
										}
									} else if (setCurrentChat) {
										if (reopened === false) {
											reopened = true;
											setCurrentChat(existingRequest);
										}
									}
								}
							}
						});
						if (proceed) {
							const id = uuidv4();
							const newChatRequest: ChatRequest = {
								id,
								users: [uid, recipientUid],
								createdBy: uid,
								hosts: isModerator ? [uid] : [],
								userName,
								recipientUserName,
								message,
								status: status,
								userData: chatUserData,
								timestamp: Timestamp.now(),
								showRequest: false,
								updatedAt: moment.utc().valueOf(),
								hostChat,
							};
							const chatDocRef = doc(contentChatCollection, newChatRequest.id);
							setDoc(chatDocRef, newChatRequest)
								.then(() => {
									if (setCurrentChat) {
										setCurrentChat(newChatRequest);
									}
								});
						}
					});
			}
		});
};

const updateChatRequest = (contentId: string, oldUid: string, uid: string, userName: string) => {
	const contentChatCollection = collection(db, "chat/direct", contentId);
	const chatQuery = query(
		contentChatCollection,
		where("users", "array-contains", oldUid),
		where("status", "in", [0, 1, 3, 4])
	);

	getDocs(chatQuery)
		.then(querySnapshot => {
			let exists = false;
			if (!querySnapshot.empty) {
				const batch = writeBatch(db);
				querySnapshot.forEach(docSnap => {
					const data = docSnap.data() as ChatRequest;
					if (data.userData) {
						let muted = false;
						if (data.userData[oldUid]) {
							muted = data.userData[oldUid].muted;
							delete data.userData[oldUid];
						}
						const auth = getAuth();
						const image = auth.currentUser?.photoURL;
						data.userData[uid] = {
							name: userName,
							image: image || null,
							muted,
							status: data.status,
						};
					}
					data.users = data.users.filter(u => u !== oldUid);
					data.users.push(uid);
					if (data.createdBy === oldUid) {
						const chatDocRef = doc(contentChatCollection, docSnap.id);
						batch.set(chatDocRef, Object.assign({}, data, { userName, createdBy: uid }));
					} else {
						const chatDocRef = doc(contentChatCollection, docSnap.id);
						batch.set(chatDocRef, Object.assign({}, data, { recipientUserName: userName }));
					}
				});
				batch.commit();
			}
		});
};

const toggleChatRequestMute = (contentId: string, requestId: string, uid: string) => {
	const requestDocRef = doc(db, "chat/direct", contentId, requestId);

	getDoc(requestDocRef).then(docSnap => {
		const data = docSnap.data() as ChatRequest;
		if (data.userData) {
			data.userData[uid].muted = !data.userData[uid].muted;
		}

		setDoc(requestDocRef, data);
	});
};

const activateChatRequest = (contentId: string, requestId: string, isHost: boolean) => {
	const requestDocRef = doc(db, "chat/direct", contentId, requestId);
	const payload: Partial<ChatRequest> = { showRequest: true };
	if (isHost) {
		payload.status = ChatRequestStatus.directMessageActive;
	}
	setDoc(requestDocRef, payload, { merge: true });
};

const updateAnalyticsUserDoc = (
	user: { uid: string, displayName: string | null, email: string | null },
	contentId: string,
	extraData: Partial<AnalyticsUser>,
	contentType: ContentType,
	initFirebase: boolean = true
) => {
	const { analyticsUserId } = extraData;
	const userRef = doc(db, "analytics/users", contentId, `${analyticsUserId}`);
	getDoc(userRef).then(docSnap => {
		let data: Partial<AnalyticsUser> = {};
		if (docSnap.exists()) {
			data = docSnap.data() as AnalyticsUser;
			data.name = user.displayName || data.name || "";
			data.email = user.email || data.email || "";
			data.firebaseId = user.uid;
			data = Object.assign({}, data, extraData);
		} else {
			data.name = user.displayName || "";
			data.email = user.email || "";
			data.firebaseId = user.uid;
			data = Object.assign({}, data, extraData);
		}
		setDoc(userRef, data, { merge: true });
		if (initFirebase) {
			initFirebasePresence(contentId, analyticsUserId || "", user.uid, contentType);
		}
	});
};

export {
	fetchClient,
	fetchChannel,
	fetchChannels,
	fetchContent,
	fetchContents,
	fetchTags,
	createChatRequest,
	createDirectMessageChatRequest,
	updateChatRequest,
	updateAnalyticsUserDoc,
	toggleChatRequestMute,
	activateChatRequest,
};
